import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Silence benign ResizeObserver loop warnings — Chrome/Chromium fires these when a
// ResizeObserver callback triggers layout changes within the same frame. Harmless,
// but CRA's dev overlay catches them and shows a fake "Uncaught runtime error".
const resizeObserverErrorRe = /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

// 1) Wrap ResizeObserver so callbacks run in the next frame — prevents the loop entirely.
if (typeof window !== "undefined" && "ResizeObserver" in window) {
  const OrigResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class DebouncedResizeObserver extends OrigResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          callback(entries, observer);
        });
      });
    }
  };
}

// 2) Belt-and-suspenders: swallow the error if it still leaks (CRA overlay guard).
window.addEventListener("error", (e) => {
  if (e && e.message && resizeObserverErrorRe.test(e.message)) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    return true;
  }
  return undefined;
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = (e && e.reason && (e.reason.message || String(e.reason))) || "";
  if (resizeObserverErrorRe.test(msg)) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    return true;
  }
  return undefined;
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
